<template>
  <div
    tabindex="-1"
    class="flyout-container"
    :style="flyoutContainerPosition"
  >
    <aside
      class="filter-spec-arrow-aside"
      :style="arrowAsidePosition"
    >
      <div class="filter-spec-arrow-left" />
    </aside>
    <aside
      ref="filter-spec-aside"
      class="filter-spec-aside"
      :style="filterSpecAsidePosition"
    >
      <component
        :is="components[filter.type]"
        v-if="components[filter.type]"
        :id="filter.label"
        ref="filter-spec"
        :filter="filter"
        :collection="collection"
        class="filter-spec"
        @close-filter-specification="$emit('close-filter-specification')"
        @update-filters="(filter) => $emit('update-filters', filter)"
      />
    </aside>
    <svg xmlns="http://www.w3.org/2000/svg">
      <filter id="flyoutBorderFilter">
        <feMorphology
          operator="dilate"
          radius="1 1"
          in="SourceGraphic"
          result="morphology"
        />
        <feFlood
          flood-color="#405866"
          flood-opacity="0.66"
          result="flood"
        />
        <feComposite
          in="flood"
          in2="morphology"
          operator="in"
          result="composite"
        />
        <feMerge result="merge">
          <feMergeNode in="composite" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </svg>
  </div>
</template>

<script>
// Services and helpers
import {useDesktopLayout} from '@/lib/ReusableFunctions/User';

export default {
  name: 'FilterSpec',
  props: {
    filter: {
      type: Object,
      required: true,
    },
    components: {
      type: Object,
      required: true,
    },
    /** Collection filters only. Layer filters have no collection. */
    collection: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['close-filter-specification', 'update-filters'],
  data() {
    return {
      useDesktopLayout: useDesktopLayout(),
      cardBottom: 0,
      // default position for the flyout. Left enough to account for the menu width and padding, and no modification to
      // the top edge. This will be the position on mobile, but will be modified on desktop to flyout to the right.
      filterSpecAsidePosition: 'left: 20px; top: 0px;',
      parentHeight: 0,
      parentWidth: 0,
      scrollContainer: null,
    };
  },
  computed: {
    arrowAsidePosition() {
      if (!this.useDesktopLayout) {
        // the arrow should not be visible on mobile
        return `display: none;`;
      }
      const bottom = (this.parentHeight - 20) / 2;
      const left = this.parentWidth ?? 0;
      return `bottom: ${bottom}px; left: ${left}px`;
    },
    flyoutContainerPosition() {
      // the top edge of the flyout should be aligned with the bottom edge of the card, unless other constraints apply
      return `top: ${this.cardBottom}px; left: 0px;`;
    },
  },
  mounted() {
    // If we're on mobile, the fly-out wil remain in the default position tied to the bottom of the card
    if (!this.useDesktopLayout) {
      const parent = this.$parent.$el;
      this.cardBottom = parent.offsetTop + parent.offsetHeight;
    } else {
      const scrollableParent = this.getScrollableParent(this.$refs['filter-spec-aside']);
      if (scrollableParent) {
        scrollableParent.addEventListener('scroll', this.calculateFlyoutPosition);
        this.scrollContainer = scrollableParent;
      }
      this.parentWidth = this.$parent.$el.getBoundingClientRect().width;
      this.parentHeight = this.$parent.$el.getBoundingClientRect().height;
      this.calculateFlyoutPosition();
    }
  },
  beforeUnmount() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.calculateFlyoutPosition);
    }
  },
  methods: {
    /**
     * Finds the first parent of the given element that is scrollable in the Y direction.
     * @param {HTMLElement} element
     * @return {HTMLElement | null}
     */
    getScrollableParent(element) {
      if (!element || !(element instanceof HTMLElement)) return null;
      const overflowY = window.getComputedStyle(element).overflowY;
      const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
      if (isScrollable && element.scrollHeight >= element.clientHeight) {
        return element;
      }
      return this.getScrollableParent(element.parentNode);
    },
    calculateFlyoutPosition() {
      this.cardBottom = this.$parent.$el.getBoundingClientRect().bottom;

      // If we're on desktop, the flyout will be positioned to the right of the card, and will not extend past the
      // bottom of the window
      const clientRect = this.$refs['filter-spec-aside']?.getBoundingClientRect();
      const left = (this.parentWidth || 300) + 10;
      const windowHeight = window.innerHeight;

      // Ensure the flyout does not extend past the bottom of the window
      if ((this.cardBottom + clientRect?.height - this.parentHeight) > windowHeight) {
        this.filterSpecAsidePosition = `top: ${(windowHeight - this.cardBottom - clientRect?.height)}px; left: ${left}px`;
        return;
      }

      // otherwise, position the flyout to the right of and below the card
      const top = 0 - this.parentHeight;
      this.filterSpecAsidePosition = `top: ${top}px; left: ${left}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
.flyout-container {
  filter: url(#flyoutBorderFilter) drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.3));
  position: absolute;
  height: 0;
  width: 0;
  z-index: 100;
}

.filter-spec {
  background: #FFFFFF;
  min-width: 275px;
  border-radius: 5px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  &-aside {
    position: absolute;
  }
  &-arrow-aside {
    position: absolute;
  }
  &-arrow-left {
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid white;
  }
}

@include main.respond(desktop) {
  .filter-spec {
    min-width: 300px;
    &-arrow-aside {
      display: inline-block;
    }
  }
}
</style>
